/**
 * How the Central answers: rules first, AI only where it pays (D853 phase B, analysis `docs/ai/analysis/D853_central_hybrid_automation_and_city.md` section 2.2) -
 * governed key `central.automation`.
 *
 * Every message Vello handles climbs a ladder and stops at the first rung that can answer it: safety words, the audience and topic the person tapped, the person's own
 * account data, a templated answer, a knowledge-base article returned as it is, ONE cheap classification call on ambiguous text, the full model, a person. This setting
 * says how far each INTENT may climb and holds the numbers that used to be constants. The value:
 *
 *   `{ "aiEnabled": true, "defaultMode": "ai", "intents": { "<intent>": { "mode": "rules", "onMiss": "human" } },
 *      "menus": { "customer": [ { "id", "intent", "labels": { "<lang>": "..." }, "keywords": { "<lang>": ["..."] }, "prompt": { "<lang>": "..." } } ], "provider": [ ... ] },
 *      "topicIntent": { "<topic the person tapped>": "<intent> | menu" },
 *      "kbDirect": { "enabled", "minScore", "minMargin" }, "classifier": { "enabled", "minConfidencePercent", "maxChars" },
 *      "generative": { "maxTurnsPerConversation", "maxToolRounds", "minConfidencePercent", "historyMessages", "passages", "maxFailedTurns" } }`
 *
 *  - an intent's `mode` is `rules` (the code answers from the person's data and fixed texts; the model is not used), `ai` (the model conversation) or `human` (a person);
 *    `onMiss` (rules and ai only) says where the intent goes when its rules cannot finish it: `ai` (default for rules) or `human`;
 *  - `aiEnabled: false` is the AI KILL SWITCH: no model is called for anything (conversation, classification, summary); `ai` intents behave as `human`, and what the rules
 *    can answer is still answered. It does not switch Vello off (that is `central.autonomy` `enabled`);
 *  - `menus` are the options of the top menu per audience (`customer`: a rider; `provider`: a driver, courier, candidate or partner). An option names the intent it opens,
 *    its label per language (the button text), the words a person may type instead of tapping, and the question Vello asks when the intent needs the person's details;
 *    the intent `human` means "talk to someone";
 *  - `topicIntent`: what a topic the person tapped before writing (the site widget's second step) opens - an intent, or `menu` for the audience's menu;
 *  - `kbDirect`: a knowledge article whose score is at least `minScore` and beats the runner-up by `minMargin` is returned as it is, with no model;
 *  - `classifier`: one small-model call on a text the rules cannot place; a result under `minConfidencePercent` goes on to the full model;
 *  - `generative`: the bounds of the full-model conversation (turns, tool rounds, the confidence below which a person takes over, history and passages given to the model,
 *    consecutive failed turns before a person takes over).
 *
 * A settings-kernel key (D393): platform and country scope, OVERRIDABLE, four eyes on every change. The country version wins per intent and per menu; the platform fills
 * the rest. What a lower scope can NEVER do: switch the AI back on (the kill switch is the AND of the buckets) or lower a quality threshold the platform set (confidence and
 * score thresholds take the higher value; turn and round limits the lower). The registry default (D865) is AI on and every intent through the model, no menu, no
 * classifier, today's limits - except that a published knowledge article that clearly answers the question is returned as it is (`faq: rules`, `kbDirect` on). A version the reader cannot parse fails CLOSED for the AI (`CENTRAL_AUTOMATION_INVALID_AI_OFF`):
 * nothing goes to a model, nothing is returned direct; people take over. Neutral: no country, language list or intent catalogue is named here.
 */
import { COUNTRY_RE, effectiveBucket, isObj, isPlainText, onlyMembers, scopedSetting } from './central-ai-shared.js';
import { INTENT_TOKEN_RE } from './central-autonomy-policy.js';
/** The governed key. */
export const CENTRAL_AUTOMATION_KEY = 'central.automation';
export const CENTRAL_AUTOMATION_MODES = ['rules', 'ai', 'human'];
export const CENTRAL_AUTOMATION_MISS_TARGETS = ['ai', 'human'];
/** The two menus: a rider reads `customer`; a driver, courier, candidate or partner reads `provider`. */
export const AUTOMATION_AUDIENCES = ['customer', 'provider'];
/** The intent a menu option uses for "talk to someone". */
export const HUMAN_INTENT = 'human';
/** The value of a topic that opens the audience's menu rather than one intent. */
export const MENU_TARGET = 'menu';
export const CENTRAL_AUTOMATION_BOUNDS = {
    maxIntents: 60,
    maxMenuOptions: 8,
    maxTopics: 40,
    maxLanguages: 8,
    label: { min: 2, max: 40 },
    prompt: { min: 2, max: 300 },
    keyword: { min: 2, max: 30 },
    maxKeywordsPerLanguage: 12,
    kbDirect: { minScore: { min: 1, max: 100 }, minMargin: { min: 0, max: 100 } },
    classifier: { minConfidencePercent: { min: 50, max: 100 }, maxChars: { min: 100, max: 1000 } },
    generative: {
        maxTurnsPerConversation: { min: 1, max: 200 },
        maxToolRounds: { min: 1, max: 5 },
        minConfidencePercent: { min: 50, max: 95 },
        historyMessages: { min: 2, max: 20 },
        passages: { min: 1, max: 8 },
        maxFailedTurns: { min: 1, max: 10 },
    },
};
/**
 * The registry default (D865): AI on, every intent through the model (no menu, no classifier, the limits that were constants) EXCEPT the question a published knowledge article
 * answers, which is returned as it is by the knowledge rung (`faq` is `rules`, `kbDirect` is on, minScore 8 and minMargin 3: a single word scores 6 at most - title 3, tag 3; the body does not count for the direct answer, and two distinct words are required - so it can never
 * decide an answer alone, e.g. "where is my driver" against an article about driver sign-up; D853 proposed 6, which one title-and-tag word reaches) - before the AI-processing consent and with no
 * model call. Until D865 this was off, so a deployment that had published nothing sent every question to the model and the answer stood or fell on the model's citation (staging, D857:
 * four of four turns ended `VELO_HANDOFF_NOT_GROUNDED` although the article existed). What an answer may do is still `central.autonomy`'s: in shadow the article is a draft for the agent.
 */
export const DEFAULT_CENTRAL_AUTOMATION = {
    aiEnabled: true,
    defaultMode: 'ai',
    intents: { faq: { mode: 'rules', onMiss: 'ai' } },
    menus: { customer: [], provider: [] },
    topicIntent: {},
    kbDirect: { enabled: true, minScore: 8, minMargin: 3 },
    classifier: { enabled: false, minConfidencePercent: 75, maxChars: 400 },
    generative: { maxTurnsPerConversation: 50, maxToolRounds: 3, minConfidencePercent: 60, historyMessages: 8, passages: 4, maxFailedTurns: 3 },
};
export const CENTRAL_AUTOMATION_REASON_CODES = ['CENTRAL_AUTOMATION_GOVERNED', 'CENTRAL_AUTOMATION_DEFAULT', 'CENTRAL_AUTOMATION_INVALID_AI_OFF'];
const LANG_RE = /^[a-z]{2}$/;
const OPTION_ID_RE = /^[a-z][a-z0-9_]{0,29}$/;
const isMode = (v) => typeof v === 'string' && CENTRAL_AUTOMATION_MODES.includes(v);
const isMiss = (v) => typeof v === 'string' && CENTRAL_AUTOMATION_MISS_TARGETS.includes(v);
const inRange = (v, r) => typeof v === 'number' && Number.isFinite(v) && v >= r.min && v <= r.max;
const isInt = (v, r) => inRange(v, r) && Number.isInteger(v);
function localizedIssue(v, r) {
    if (!isObj(v))
        return true;
    const entries = Object.entries(v);
    if (entries.length < 1 || entries.length > CENTRAL_AUTOMATION_BOUNDS.maxLanguages)
        return true;
    return !entries.every(([lang, text]) => LANG_RE.test(lang) && isPlainText(text, r.min, r.max));
}
function optionIssue(o) {
    if (!isObj(o))
        return 'INVALID_MENU_OPTION';
    if (!onlyMembers(o, ['id', 'intent', 'labels', 'keywords', 'prompt']) || !('id' in o) || !('intent' in o) || !('labels' in o))
        return 'INVALID_MENU_OPTION';
    if (typeof o.id !== 'string' || !OPTION_ID_RE.test(o.id) || typeof o.intent !== 'string' || !INTENT_TOKEN_RE.test(o.intent))
        return 'INVALID_MENU_OPTION';
    if (localizedIssue(o.labels, CENTRAL_AUTOMATION_BOUNDS.label))
        return 'INVALID_MENU_LABELS';
    if ('keywords' in o) {
        const k = o.keywords;
        if (!isObj(k) || Object.keys(k).length > CENTRAL_AUTOMATION_BOUNDS.maxLanguages)
            return 'INVALID_MENU_KEYWORDS';
        for (const [lang, words] of Object.entries(k)) {
            if (!LANG_RE.test(lang) || !Array.isArray(words) || words.length < 1 || words.length > CENTRAL_AUTOMATION_BOUNDS.maxKeywordsPerLanguage)
                return 'INVALID_MENU_KEYWORDS';
            if (!words.every((w) => isPlainText(w, CENTRAL_AUTOMATION_BOUNDS.keyword.min, CENTRAL_AUTOMATION_BOUNDS.keyword.max)) || new Set(words).size !== words.length)
                return 'INVALID_MENU_KEYWORDS';
        }
    }
    if ('prompt' in o && localizedIssue(o.prompt, CENTRAL_AUTOMATION_BOUNDS.prompt))
        return 'INVALID_MENU_PROMPT';
    return undefined;
}
function menusIssue(m) {
    if (!isObj(m) || !onlyMembers(m, [...AUTOMATION_AUDIENCES]) || !AUTOMATION_AUDIENCES.every((a) => Array.isArray(m[a])))
        return 'INVALID_MENUS';
    for (const audience of AUTOMATION_AUDIENCES) {
        const options = m[audience];
        if (options.length > CENTRAL_AUTOMATION_BOUNDS.maxMenuOptions)
            return 'TOO_MANY_MENU_OPTIONS';
        const ids = new Set();
        for (const o of options) {
            const issue = optionIssue(o);
            if (issue !== undefined)
                return issue;
            const id = o.id;
            if (ids.has(id))
                return 'DUPLICATE_MENU_OPTION';
            ids.add(id);
        }
    }
    return undefined;
}
/** The stable issue of a value that is not an automation value, or undefined when it is one. */
export function centralAutomationIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    const members = ['aiEnabled', 'defaultMode', 'intents', 'menus', 'topicIntent', 'kbDirect', 'classifier', 'generative'];
    if (!onlyMembers(v, members) || !members.every((m) => m in v))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.aiEnabled !== 'boolean')
        return 'INVALID_AI_ENABLED';
    if (!isMode(v.defaultMode))
        return 'INVALID_DEFAULT_MODE';
    if (!isObj(v.intents))
        return 'INTENTS_NOT_AN_OBJECT';
    const entries = Object.entries(v.intents);
    if (entries.length > CENTRAL_AUTOMATION_BOUNDS.maxIntents)
        return 'TOO_MANY_INTENTS';
    for (const [intent, entry] of entries) {
        if (!INTENT_TOKEN_RE.test(intent))
            return 'INVALID_INTENT_ID';
        if (!isObj(entry) || !onlyMembers(entry, ['mode', 'onMiss']) || !isMode(entry.mode))
            return 'INVALID_INTENT_MODE';
        if ('onMiss' in entry && (!isMiss(entry.onMiss) || entry.mode === 'human'))
            return 'INVALID_ON_MISS';
    }
    const menus = menusIssue(v.menus);
    if (menus !== undefined)
        return menus;
    if (!isObj(v.topicIntent) || Object.keys(v.topicIntent).length > CENTRAL_AUTOMATION_BOUNDS.maxTopics)
        return 'INVALID_TOPIC_INTENT';
    for (const [topic, target] of Object.entries(v.topicIntent)) {
        if (!INTENT_TOKEN_RE.test(topic) || typeof target !== 'string' || !INTENT_TOKEN_RE.test(target))
            return 'INVALID_TOPIC_INTENT';
    }
    const kb = v.kbDirect;
    if (!isObj(kb) || !onlyMembers(kb, ['enabled', 'minScore', 'minMargin']) || typeof kb.enabled !== 'boolean' || !isInt(kb.minScore, CENTRAL_AUTOMATION_BOUNDS.kbDirect.minScore) || !isInt(kb.minMargin, CENTRAL_AUTOMATION_BOUNDS.kbDirect.minMargin))
        return 'INVALID_KB_DIRECT';
    const cl = v.classifier;
    if (!isObj(cl) || !onlyMembers(cl, ['enabled', 'minConfidencePercent', 'maxChars']) || typeof cl.enabled !== 'boolean' || !isInt(cl.minConfidencePercent, CENTRAL_AUTOMATION_BOUNDS.classifier.minConfidencePercent) || !isInt(cl.maxChars, CENTRAL_AUTOMATION_BOUNDS.classifier.maxChars))
        return 'INVALID_CLASSIFIER';
    const g = v.generative;
    const b = CENTRAL_AUTOMATION_BOUNDS.generative;
    if (!isObj(g) || !onlyMembers(g, ['maxTurnsPerConversation', 'maxToolRounds', 'minConfidencePercent', 'historyMessages', 'passages', 'maxFailedTurns']) ||
        !isInt(g.maxTurnsPerConversation, b.maxTurnsPerConversation) || !isInt(g.maxToolRounds, b.maxToolRounds) || !isInt(g.minConfidencePercent, b.minConfidencePercent) ||
        !isInt(g.historyMessages, b.historyMessages) || !isInt(g.passages, b.passages) || !isInt(g.maxFailedTurns, b.maxFailedTurns))
        return 'INVALID_GENERATIVE';
    return undefined;
}
export function validCentralAutomationValue(v) {
    return centralAutomationIssue(v) === undefined;
}
/** The value normalised: intents sorted, so equal meaning is equal content (and one content hash). */
function normalise(v) {
    return {
        aiEnabled: v.aiEnabled,
        defaultMode: v.defaultMode,
        intents: Object.fromEntries(Object.keys(v.intents).sort().map((k) => [k, v.intents[k]])),
        menus: { customer: v.menus.customer.map((o) => ({ ...o })), provider: v.menus.provider.map((o) => ({ ...o })) },
        topicIntent: Object.fromEntries(Object.keys(v.topicIntent).sort().map((k) => [k, v.topicIntent[k]])),
        kbDirect: { ...v.kbDirect },
        classifier: { ...v.classifier },
        generative: { ...v.generative },
    };
}
/** Build a valid version (platform or country scope). Throws a RangeError on a malformed scope ref or value. */
export function centralAutomationSetting(input) {
    const issue = centralAutomationIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid Central automation: ${issue}`);
    return scopedSetting({
        key: CENTRAL_AUTOMATION_KEY, scope: input.scope, ...(input.scopeRef !== undefined ? { scopeRef: input.scopeRef } : {}), value: normalise(input.value), version: input.version,
        effectiveFrom: input.effectiveFrom, ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}), mode: 'OVERRIDABLE',
    });
}
/** An unreadable version: nothing goes to a model and nothing is returned direct (D865: the default's knowledge rung is switched off here too); people take over. */
const FAIL_CLOSED_VALUE = { ...DEFAULT_CENTRAL_AUTOMATION, aiEnabled: false, defaultMode: 'human', intents: {}, kbDirect: { ...DEFAULT_CENTRAL_AUTOMATION.kbDirect, enabled: false } };
/**
 * The automation in force for `country` at `at`. Both buckets are read (country first); either one unreadable fails closed for the AI. Merge: `aiEnabled` is the AND; the
 * country's intent entry wins over the platform's; a menu is the country's when it has options, else the platform's; the thresholds take the stricter of the buckets.
 */
export function resolveCentralAutomation(settings, country, at) {
    const closed = (suffix) => ({ reasonCode: 'CENTRAL_AUTOMATION_INVALID_AI_OFF', policyVersion: `${CENTRAL_AUTOMATION_KEY}@${suffix}`, aiEnabled: false, value: FAIL_CLOSED_VALUE });
    if (!COUNTRY_RE.test(country))
        return closed('invalid-ai-off');
    const found = [];
    for (const b of [effectiveBucket(settings, CENTRAL_AUTOMATION_KEY, 'country', country, at), effectiveBucket(settings, CENTRAL_AUTOMATION_KEY, 'platform', undefined, at)]) {
        if (b === undefined)
            continue;
        if (!validCentralAutomationValue(b.value))
            return closed('invalid-ai-off');
        found.push(b);
    }
    if (found.length === 0)
        return { reasonCode: 'CENTRAL_AUTOMATION_DEFAULT', policyVersion: `${CENTRAL_AUTOMATION_KEY}@default`, aiEnabled: DEFAULT_CENTRAL_AUTOMATION.aiEnabled, value: DEFAULT_CENTRAL_AUTOMATION };
    const values = found.map((b) => normalise(b.value)); // country first, then platform
    const first = values[0];
    const platformValue = values.length > 1 ? values[1] : undefined;
    const aiEnabled = values.every((v) => v.aiEnabled);
    const pick = (f) => f(first);
    const strict = (f, higher) => (higher ? Math.max : Math.min)(...values.map(f));
    const menu = (audience) => values.map((v) => v.menus[audience]).find((m) => m.length > 0) ?? [];
    const merged = {
        aiEnabled,
        defaultMode: pick((v) => v.defaultMode),
        intents: Object.fromEntries(Object.entries({ ...(platformValue?.intents ?? {}), ...first.intents }).sort(([a], [b]) => a.localeCompare(b))),
        menus: { customer: menu('customer'), provider: menu('provider') },
        topicIntent: Object.fromEntries(Object.entries({ ...(platformValue?.topicIntent ?? {}), ...first.topicIntent }).sort(([a], [b]) => a.localeCompare(b))),
        kbDirect: { enabled: pick((v) => v.kbDirect.enabled), minScore: strict((v) => v.kbDirect.minScore, true), minMargin: strict((v) => v.kbDirect.minMargin, true) },
        classifier: { enabled: pick((v) => v.classifier.enabled), minConfidencePercent: strict((v) => v.classifier.minConfidencePercent, true), maxChars: pick((v) => v.classifier.maxChars) },
        generative: {
            maxTurnsPerConversation: strict((v) => v.generative.maxTurnsPerConversation, false),
            maxToolRounds: strict((v) => v.generative.maxToolRounds, false),
            minConfidencePercent: strict((v) => v.generative.minConfidencePercent, true),
            historyMessages: pick((v) => v.generative.historyMessages),
            passages: pick((v) => v.generative.passages),
            maxFailedTurns: strict((v) => v.generative.maxFailedTurns, false),
        },
    };
    return { reasonCode: 'CENTRAL_AUTOMATION_GOVERNED', policyVersion: found.map((b) => b.policyVersion).join(';'), aiEnabled, value: merged };
}
/**
 * The mode of one intent: its entry, else the default; the AI kill switch turns `ai` into `human` and a rules intent's miss into `human`.
 * A rules intent with no `onMiss` goes on to the model.
 */
export function modeForIntent(decision, intent) {
    const entry = decision.value.intents[intent];
    const mode = entry?.mode ?? decision.value.defaultMode;
    if (mode === 'human')
        return { mode: 'human' };
    if (mode === 'ai')
        return decision.aiEnabled ? { mode: 'ai' } : { mode: 'human' };
    const onMiss = entry?.onMiss ?? 'ai';
    return { mode: 'rules', onMiss: decision.aiEnabled ? onMiss : 'human' };
}
/** The menu a subject role reads: a rider (or nobody known) the customer menu; a driver, courier, candidate or partner the provider menu. */
export function automationAudienceOf(role) {
    return role === undefined || role === 'rider' ? 'customer' : 'provider';
}
//# sourceMappingURL=central-automation-policy.js.map